import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  IconButton,
  TextField,
  Paper,
  Typography,
  CircularProgress,
  Fab,
} from '@mui/material';
import ChatIcon from '@mui/icons-material/Chat';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import { postAvaApiChat } from '../../../clients/bettercallkyle/BckAvaApi';
import { getUserId } from '../../../selectors/UserSelectors';
import { StyledTextField } from '../../common/StyledTextField';

const AdminChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);

  // Scroll to bottom when messages update
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, loading]);

  const sendMessage = async () => {
    if (!input.trim()) return;

    const userMessage = { role: 'user', content: input };
    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const data = await postAvaApiChat(getUserId(), input, messages);
      const assistantMessage = { role: 'assistant', content: data.reply };
      setMessages((prev) => [...prev, assistantMessage]);
    } catch (err) {
      console.error('Chat error:', err);
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: 'Something went wrong. Please try again later.' },
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Floating Button */}
      <Fab
        color="primary"
        onClick={() => setIsOpen((prev) => !prev)}
        sx={{ position: 'fixed', bottom: 16, right: 16, zIndex: 1300 }}
      >
        {isOpen ? <CloseIcon /> : <ChatIcon />}
      </Fab>

      {/* Chat Panel */}
      {isOpen && (
        <Paper
          elevation={6}
          sx={{
            position: 'fixed',
            bottom: 80,
            right: 16,
            width: 320,
            height: 400,
            display: 'flex',
            flexDirection: 'column',
            zIndex: 1200,
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          {/* Message List */}
          <Box
            sx={{
              flex: 1,
              padding: 2,
              overflowY: 'auto',
              backgroundColor: '#f5f5f5',
            }}
          >
            {messages.map((m, i) => (
              <Box
                key={i}
                sx={{
                  display: 'flex',
                  justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start',
                  mb: 1,
                }}
              >
                <Paper
                  sx={{
                    p: 1,
                    maxWidth: '80%',
                    bgcolor: m.role === 'user' ? '#1976d2' : '#e0e0e0',
                    color: m.role === 'user' ? '#fff' : '#000',
                    borderRadius: 2,
                  }}
                >
                  <p style={{ margin: '5px' }}>
                    {m.content}
                  </p>
                </Paper>
              </Box>
            ))}
            {loading && <Typography variant="body2">Assistant is typing...</Typography>}
            <div ref={messagesEndRef} />
          </Box>

          {/* Input Area */}
          <Box sx={{ display: 'flex', p: 1, borderTop: '1px solid #ddd' }}>
            <StyledTextField
              fullWidth
              variant="outlined"
              size="small"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
              placeholder="Type a message"
            />
            <IconButton onClick={sendMessage} color="primary" disabled={loading}>
              {loading ? <CircularProgress size={20} /> : <SendIcon />}
            </IconButton>
          </Box>
        </Paper>
      )}
    </>
  );
};

export default AdminChatWidget;
